/**
 * HQ Shade Engine
 * Optional shade swatch tiles and shade/texture gallery filtering for any product template.
 * Configured per template by sections/hq-shade-engine.liquid. When both features are
 * switched off in the Theme Editor, the section outputs nothing and this file is not loaded.
 *
 * Media naming convention: {line}-{texture}-{shade code}-{NN}.ext
 *   e.g. bulkhair-beachwave-1b-01.webp, clipins-straight-b2-18-03.jpg
 * Texture and shade tokens are optional; files with neither are "neutral" shots.
 */
(() => {
  'use strict';

  if (window.HqShadeEngine) {
    window.HqShadeEngine.scan();
    return;
  }

  /* ---------- helpers ---------- */

  const splitNames = (s) =>
    String(s || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);

  const compact = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const slug = (s) =>
    String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  // Shade code rule (must match Liquid): text after the last "#", else the last word, handleized.
  // "Chestnut Red #34" -> "34", "Rooted Ash Blonde B2/18" -> "b2-18", "Natural Black #1B" -> "1b"
  const shadeCode = (value) => {
    const v = String(value || '').trim();
    const token = v.includes('#') ? v.split('#').pop() : v.split(/\s+/).pop();
    return slug(token);
  };

  // Dash-delimited file key: ".../bulkhair-beachwave-1-01.webp?v=1" -> "-bulkhair-beachwave-1-01-"
  const fileKey = (src) => {
    let name = String(src || '').split('?')[0].split('#')[0].split('/').pop() || '';
    name = name
      .replace(/\.[a-z0-9]+$/i, '')
      .toLowerCase()
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, '') // Shopify UUID dedupe
      .replace(/_(\d+x\d*|x\d+)$/, '') // legacy size suffix
      .replace(/_\d+$/, ''); // Shopify numeric dedupe
    return name ? `-${slug(name)}-` : '';
  };

  // One load check per swatch URL for the life of the page, so missing files cost one 404 each.
  const swatchCache = new Map();
  const probe = (url) => {
    if (!swatchCache.has(url)) {
      swatchCache.set(
        url,
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url;
        })
      );
    }
    return swatchCache.get(url);
  };

  // A theme's own swatch may be an <img>, an <svg>, or — most often — a background
  // image on the label or on a ::before/::after. Anything visual here means the
  // control already has a swatch, and a second one must not be added beside it.
  const hasOwnSwatch = (root) => {
    if (root.querySelector('img, svg, .hq-swatch-tile')) return true;
    const nodes = [root, ...root.querySelectorAll('*')].slice(0, 40);
    return nodes.some((n) =>
      [null, '::before', '::after'].some((pseudo) => {
        let bg = '';
        try {
          bg = getComputedStyle(n, pseudo).backgroundImage || '';
        } catch (err) {
          return false;
        }
        return bg !== '' && bg !== 'none';
      })
    );
  };

  const mediaSrc = (el) => {
    if (el.dataset.hqMediaSrc) return el.dataset.hqMediaSrc;
    const img = el.matches('img') ? el : el.querySelector('img');
    if (!img) return '';
    return (
      img.getAttribute('src') ||
      img.dataset.src ||
      (img.getAttribute('srcset') || img.dataset.srcset || '').trim().split(/\s+/)[0] ||
      ''
    );
  };

  /* ---------- engine ---------- */

  class Engine {
    constructor(configEl) {
      this.configEl = configEl;
      this.cfg = JSON.parse(configEl.textContent);
      this.strips = [];
      this.timer = null;
      this.lateTimer = null;

      const p = this.cfg.product;
      const colorNames = splitNames(this.cfg.colorNames);
      const textureNames = splitNames(this.cfg.textureNames);

      this.options = p.options;
      this.colorIdx = p.options.findIndex((o) => colorNames.includes(String(o.name).toLowerCase()));
      this.textureIdx = p.options.findIndex((o) => textureNames.includes(String(o.name).toLowerCase()));
      this.colorCodes =
        this.colorIdx > -1 ? p.options[this.colorIdx].values.map(shadeCode).filter(Boolean) : [];
      this.textureTokens =
        this.textureIdx > -1
          ? p.options[this.textureIdx].values.map((v) => [compact(v), slug(v)]).filter(([c]) => c)
          : [];
      this.variants = new Map(p.variants.map((v) => [String(v.id), v.options]));

      if (this.colorIdx < 0 && this.textureIdx < 0) return;
      this.bind();
      this.refresh();
    }

    /* ----- DOM lookup ----- */

    form() {
      const forms = [...document.querySelectorAll(this.cfg.formSelector || 'form[action*="/cart/add"]')];
      const withId = forms.filter((f) => f.querySelector('[name="id"]'));
      return (
        withId.find((f) => this.variants.has(String(f.querySelector('[name="id"]').value))) ||
        withId.find((f) => f.closest('main')) ||
        null
      );
    }

    scope() {
      const f = this.form();
      return (f && f.closest('.shopify-section')) || this.configEl.closest('main') || document;
    }

    optionControls(idx) {
      const opt = this.options[idx];
      const name = String(opt.name).toLowerCase();
      const values = opt.values;
      const others = new Set(this.options.filter((_, i) => i !== idx).flatMap((o) => o.values));
      const els = [...this.scope().querySelectorAll('input[type="radio"], select')];

      const hasValue = (el) =>
        el.tagName === 'SELECT'
          ? [...el.options].some((o) => values.includes(o.value))
          : values.includes(el.value);

      const named = (el) => {
        if (String(el.name || '').toLowerCase().includes(name)) return true;
        const legend = el.closest('fieldset')?.querySelector('legend');
        return !!legend && legend.textContent.toLowerCase().includes(name);
      };

      let hits = els.filter((el) => named(el) && hasValue(el));
      if (!hits.length) {
        hits = els.filter(
          (el) => hasValue(el) && (el.tagName === 'SELECT' || !others.has(el.value))
        );
      }
      return hits;
    }

    readDom(idx) {
      for (const el of this.optionControls(idx)) {
        if (el.tagName === 'SELECT') return el.value;
        if (el.checked) return el.value;
      }
      return '';
    }

    // Custom pickers (e.g. hq-bundle-builder) can expose the current value through
    // data-shade-current / data-texture-current (or data-hq-current-color / -texture).
    // The attribute value is used if set, otherwise the element text. Only real option values count.
    readHook(idx, attrs) {
      if (idx < 0) return '';
      const values = this.options[idx].values;
      const nodes = this.scope().querySelectorAll(attrs.map((a) => `[${a}]`).join(', '));
      for (const n of nodes) {
        const attrValue = attrs.map((a) => n.getAttribute(a)).find((v) => v && v.trim());
        const v = (attrValue || n.textContent || '').trim();
        if (values.includes(v)) return v;
      }
      return '';
    }

    selection() {
      const f = this.form();
      const id =
        f?.querySelector('[name="id"]')?.value ||
        new URLSearchParams(window.location.search).get('variant');
      const opts = id ? this.variants.get(String(id)) : null;
      // Order: custom picker hooks, then radios/selects, then the resolved variant.
      const read = (idx) => (idx < 0 ? '' : this.readDom(idx) || (opts ? opts[idx] : ''));
      return {
        color:
          this.readHook(this.colorIdx, ['data-hq-current-color', 'data-shade-current']) || read(this.colorIdx),
        texture:
          this.readHook(this.textureIdx, ['data-hq-current-texture', 'data-texture-current']) ||
          read(this.textureIdx),
      };
    }

    /* ----- gallery ----- */

    galleryItems() {
      const sel = this.cfg.gallerySelector;
      if (!sel) return [];
      let items = [...this.scope().querySelectorAll(sel)];
      if (!items.length) items = [...document.querySelectorAll(sel)];
      return items.filter((el) => !el.closest('[data-hq-shade-ignore]'));
    }

    classify(key) {
      let shade = '';
      for (const c of this.colorCodes) {
        if (key.includes(`-${c}-`) && c.length > shade.length) shade = c; // longest match wins
      }
      let texture = '';
      for (const [c, s] of this.textureTokens) {
        if ((key.includes(`-${c}-`) || key.includes(`-${s}-`)) && c.length > texture.length) texture = c;
      }
      return { shade, texture };
    }

    filterGallery({ color, texture }) {
      const items = this.galleryItems().map((el) => {
        const key = fileKey(mediaSrc(el));
        return { el, ...(key ? this.classify(key) : { shade: '', texture: '' }) };
      });
      if (!items.length) return;

      const code = color ? shadeCode(color) : '';
      const tex = texture ? compact(texture) : '';
      const labeled = items.filter((i) => i.shade || i.texture);
      const neutral = items.filter((i) => !i.shade && !i.texture);

      const tiers = [
        (i) => tex && code && i.texture === tex && i.shade === code, // texture + shade
        (i) => tex && i.texture === tex && !i.shade, // texture, no shade in file name
        (i) => tex && i.texture === tex, // texture, any shade
        (i) => code && i.shade === code, // shade only
      ];

      let match = [];
      for (const test of tiers) {
        match = labeled.filter(test);
        if (match.length) break;
      }

      const visible = new Set(
        (match.length ? match.concat(this.cfg.keepNeutral ? neutral : []) : items).map((i) => i.el)
      );

      let changed = false;
      items.forEach(({ el }) => {
        const hide = !visible.has(el);
        if (hide !== el.hasAttribute('data-hq-shade-hidden')) {
          el.toggleAttribute('data-hq-shade-hidden', hide);
          changed = true;
        }
      });

      if (!changed) return;
      const first = items.find((i) => visible.has(i.el));
      const track = first?.el.parentElement;
      if (track && track.scrollWidth > track.clientWidth) track.scrollTo({ left: 0, behavior: 'smooth' });
      window.dispatchEvent(new Event('resize')); // let sliders recalculate
      document.dispatchEvent(
        new CustomEvent('hq:gallery-filtered', { detail: { color, texture, visible: [...visible] } })
      );
    }

    /* ----- swatches ----- */

    swatchUrl(value) {
      const code = shadeCode(value);
      if (!code || !this.cfg.filesBase) return '';
      // Ask the CDN for a small rendition: source tiles can be several hundred KB.
      return `${this.cfg.filesBase}${this.cfg.swatchPrefix || ''}${code}${this.cfg.swatchSuffix || ''}.jpg?width=96`;
    }

    tile(url) {
      const span = document.createElement('span');
      span.className = 'hq-swatch-tile';
      span.setAttribute('aria-hidden', 'true');
      const img = new Image();
      img.alt = '';
      img.decoding = 'async';
      img.src = url; // already loaded by probe()
      span.appendChild(img);
      return span;
    }

    decorate() {
      if (!this.cfg.swatches || this.colorIdx < 0) return;
      this.optionControls(this.colorIdx).forEach((el) => {
        if (el.tagName === 'SELECT') {
          this.decorateSelect(el);
          return;
        }
        if (el.dataset.hqSwatchDone) return;
        el.dataset.hqSwatchDone = '1';
        const label =
          (el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`)) || el.closest('label');
        if (!label || hasOwnSwatch(label)) return; // theme already has a swatch here
        const url = this.swatchUrl(el.value);
        if (!url) return;
        // Touch the label only once the file is confirmed to exist.
        probe(url).then((ok) => {
          if (!ok || !label.isConnected || label.querySelector('.hq-swatch-tile')) return;
          label.classList.add('hq-swatch-label');
          label.prepend(this.tile(url));
        });
      });
    }

    decorateSelect(select) {
      if (select.dataset.hqSwatchDone) return;
      select.dataset.hqSwatchDone = '1';
      // A theme that already draws its own swatch strip beside the select keeps it.
      const field = select.closest('.select, fieldset, .product-form__input') || select.parentElement;
      if (field && hasOwnSwatch(field)) return;
      const opt = this.options[this.colorIdx];
      const entries = [...select.options]
        .filter((o) => opt.values.includes(o.value))
        .map((o) => ({ value: o.value, url: this.swatchUrl(o.value) }))
        .filter((e) => e.url);
      if (!entries.length) return;

      // Build the strip only from swatches that exist; insert nothing if none do.
      Promise.all(entries.map((e) => probe(e.url))).then((results) => {
        const found = entries.filter((_, i) => results[i]);
        if (!found.length || !select.isConnected) return;

        const strip = document.createElement('div');
        strip.className = 'hq-swatch-strip';
        strip.setAttribute('role', 'group');
        strip.setAttribute('aria-label', opt.name);

        found.forEach(({ value, url }) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'hq-swatch-btn';
          btn.title = value;
          btn.setAttribute('aria-label', value);
          btn.dataset.value = value;
          btn.appendChild(this.tile(url));
          btn.addEventListener('click', () => {
            if (select.value === value) return;
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.sync();
          });
          strip.appendChild(btn);
        });

        (select.closest('.select') || select).before(strip);
        this.strips.push({ select, strip });
        this.sync();
      });
    }

    sync() {
      this.strips = this.strips.filter(({ strip }) => strip.isConnected);
      this.strips.forEach(({ select, strip }) => {
        strip.querySelectorAll('.hq-swatch-btn').forEach((btn) => {
          btn.setAttribute('aria-pressed', String(btn.dataset.value === select.value));
        });
      });
    }

    /* ----- lifecycle ----- */

    bind() {
      const schedule = () => this.schedule();
      document.addEventListener('change', schedule, true);
      document.addEventListener('click', schedule, true);
      window.addEventListener('popstate', schedule);

      // Themes re-render the picker and gallery on variant change; re-apply after that.
      const target = this.scope() === document ? document.body : this.scope();
      new MutationObserver((records) => {
        if (records.some((r) => r.addedNodes.length)) this.schedule();
      }).observe(target, { childList: true, subtree: true });
    }

    schedule() {
      clearTimeout(this.timer);
      clearTimeout(this.lateTimer);
      this.timer = setTimeout(() => this.refresh(), 60);
      this.lateTimer = setTimeout(() => this.refresh(), 450);
    }

    refresh() {
      this.decorate();
      this.sync();
      if (this.cfg.gallery) this.filterGallery(this.selection());
    }
  }

  /* ---------- registry ---------- */

  const engines = new WeakMap();

  const scan = () => {
    document.querySelectorAll('script[data-hq-shade-engine]').forEach((el) => {
      if (engines.has(el)) return;
      try {
        engines.set(el, new Engine(el));
      } catch (err) {
        console.warn('[hq-shade-engine]', err);
      }
    });
  };

  window.HqShadeEngine = { scan, shadeCode, fileKey };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  document.addEventListener('shopify:section:load', scan);
})();